import { z } from "zod";

import { ExtractAtomicOperationFromDefinition } from "../atomic-operations/AtomicOperationDefinition.js";
import { keyValueUpdatePayloadSchema } from "../atomic-operations/keyValueUpdateSchema.js";
import { CellReferencesParseError, CellReferencesV2 } from "../code/index.js";
import {
  CalcsDefinition,
  ColumnAggregationsDefinition,
  ColumnDisplayFormat,
  ConditionalFormattingDefinition,
  SortDirection,
  TableFiltersDefinition,
} from "../display-table/index.js";
import {
  CellId,
  DisplayTableColumnId,
  DisplayTableConfigId,
} from "../idTypeBrands.js";
import { SemanticCap } from "../semanticCaps.js";
import { typedObjectKeys } from "../utils/typedObjects.js";

import { CREATE_CELL } from "./cell/index.js";
import { createHexVersionAtomicOperationDefinition } from "./HexVersionAtomicOperationDefinition.js";
import {
  zCellId,
  zChartCellId,
  zDbtMetricCellId,
  zDisplayTableColumnId,
  zDisplayTableConfigId,
  zSqlCellId,
} from "../zod/ZodIdTypeBrands.js";
import { zFromRuntype, zReadonlyArray } from "../zod/utils.js";

export interface DisplayTableConfigUpdatableFields {
  pageSize: number;
  height: number;
  hideIcons: boolean;
  defaultColumnWidth: number | null;
  hideIndex: boolean;
  sortByColumnDefault: string | null;
  sortByIndexColumnDefault: number | null;
  sortDirectionDefault: SortDirection;
  calcs: CalcsDefinition | null;
  pivotColumnOrdering: ReadonlyArray<SortDirection | null> | null;
  customColumnOrdering: ReadonlyArray<DisplayTableColumnId> | null;
  conditionalFormatting: ConditionalFormattingDefinition | null;
  conditionalFormattingReferences: CellReferencesV2 | null;
  conditionalFormattingReferencesParseError: CellReferencesParseError | null;
  filters: TableFiltersDefinition | null;
  filtersReferences: CellReferencesV2 | null;
  filtersReferencesParseError: CellReferencesParseError | null;
  pinnedColumns: ReadonlyArray<string> | null;
  hiddenColumns: ReadonlyArray<string> | null;
  pinIndexColumns: boolean;
  showAggregations: boolean;
  columnAggregations: ColumnAggregationsDefinition | null;
}

const _UpdateDisplayTableConfigSafeFields: {
  [key in keyof DisplayTableConfigUpdatableFields]: "";
} = {
  pageSize: "",
  height: "",
  hideIcons: "",
  defaultColumnWidth: "",
  hideIndex: "",
  sortByColumnDefault: "",
  sortByIndexColumnDefault: "",
  sortDirectionDefault: "",
  calcs: "",
  pivotColumnOrdering: "",
  conditionalFormatting: "",
  conditionalFormattingReferences: "",
  conditionalFormattingReferencesParseError: "",
  customColumnOrdering: "",
  filters: "",
  filtersReferences: "",
  filtersReferencesParseError: "",
  pinnedColumns: "",
  hiddenColumns: "",
  pinIndexColumns: "",
  showAggregations: "",
  columnAggregations: "",
};
export const UpdateDisplayTableConfigSafeFields = Object.keys(
  _UpdateDisplayTableConfigSafeFields,
);

const UPDATE_DISPLAY_TABLE_CONFIG_TYPE = "UPDATE_DISPLAY_TABLE_CONFIG" as const;
export const UpdateDisplayTableConfigPayload = keyValueUpdatePayloadSchema({
  shared: {
    cellId: zCellId,
    displayTableConfigId: zDisplayTableConfigId,
    suppressAutoRun: z.boolean().optional(),
  },
  fields: {
    pageSize: z.number(),
    height: z.number(),
    hideIcons: z.boolean(),
    defaultColumnWidth: z.number().nullable(),
    hideIndex: z.boolean(),
    sortByColumnDefault: z.string().nullable(),
    sortByIndexColumnDefault: z.number().nullable(),
    sortDirectionDefault: z.nativeEnum(SortDirection),
    calcs: zFromRuntype(CalcsDefinition).nullable(),
    pivotColumnOrdering: zReadonlyArray(
      z.nativeEnum(SortDirection).nullable(),
    ).nullable(),
    customColumnOrdering: zReadonlyArray(zDisplayTableColumnId).nullable(),
    conditionalFormatting: zFromRuntype(
      ConditionalFormattingDefinition,
    ).nullable(),
    conditionalFormattingReferences: zFromRuntype(CellReferencesV2).nullable(),
    conditionalFormattingReferencesParseError: zFromRuntype(
      CellReferencesParseError,
    ).nullable(),
    filters: zFromRuntype(TableFiltersDefinition).nullable(),
    filtersReferences: zFromRuntype(CellReferencesV2).nullable(),
    filtersReferencesParseError: zFromRuntype(
      CellReferencesParseError,
    ).nullable(),
    pinnedColumns: zReadonlyArray(z.string()).nullable(),
    hiddenColumns: zReadonlyArray(z.string()).nullable(),
    pinIndexColumns: z.boolean(),
    showAggregations: z.boolean(),
    columnAggregations: zFromRuntype(ColumnAggregationsDefinition).nullable(),
  } satisfies Record<keyof DisplayTableConfigUpdatableFields, z.ZodTypeAny>,
});
export type UpdateDisplayTableConfigPayload = z.infer<
  typeof UpdateDisplayTableConfigPayload
>;

export const UPDATE_DISPLAY_TABLE_CONFIG =
  createHexVersionAtomicOperationDefinition({
    type: UPDATE_DISPLAY_TABLE_CONFIG_TYPE,
    payloadSchema: UpdateDisplayTableConfigPayload,
    readAuth: {
      kind: "hasSemanticCap",
      cap: SemanticCap.VIEW_PROJECT_CONTENTS_FOR_LOGIC,
    },
    writeAuth: {
      kind: "and",
      and: [
        {
          kind: "hasSemanticCapOnIdArg",
          cap: SemanticCap.EDIT_PROJECT_CONTENTS,
          idArg: "cellId",
          idType: "Cell",
        },
        {
          kind: "hasSemanticCapOnIdArg",
          idArg: "displayTableConfigId",
          idType: "DisplayTableConfig",
          cap: SemanticCap.EDIT_PROJECT_CONTENTS,
        },
      ],
    },
    scopedIds: {
      cellId: { scope: "ownedByWrapper", idType: "Cell" },
      displayTableConfigId: {
        scope: "ownerUnresolvable",
        idType: "DisplayTableConfig",
        reason: "project-scoped, but carries no hexVersionId column",
      },
    },
    logSafe: ["cellId", "key", "displayTableConfigId"],
    conflictId: (op) =>
      `${UPDATE_DISPLAY_TABLE_CONFIG_TYPE}-${op.payload.displayTableConfigId}-${op.payload.key}`,
    creationId: (op) => `${CREATE_CELL.type}-${op.payload.cellId}`,
    create: <K extends keyof DisplayTableConfigUpdatableFields>(args: {
      cellId: CellId;
      displayTableConfigId: DisplayTableConfigId;
      key: K;
      value: DisplayTableConfigUpdatableFields[K];
      suppressAutoRun?: boolean;
    }) => ({
      type: UPDATE_DISPLAY_TABLE_CONFIG_TYPE,
      payload: {
        ...args,
      },
    }),
  });

export type UPDATE_DISPLAY_TABLE_CONFIG = ExtractAtomicOperationFromDefinition<
  typeof UPDATE_DISPLAY_TABLE_CONFIG
>;

const CREATE_DISPLAY_TABLE_CONFIG_TYPE =
  "CREATE_DISPLAY_TABLE_CONFIG_TYPE" as const;
export const CreateDisplayTableConfigPayload = z.object({
  cellId: zCellId,
  displayTableConfigId: zDisplayTableConfigId,
  suppressAutoRun: z.boolean().optional(),
  filters: zFromRuntype(TableFiltersDefinition).optional(),
});
export type CreateDisplayTableConfigPayload = z.infer<
  typeof CreateDisplayTableConfigPayload
>;

export const CREATE_DISPLAY_TABLE_CONFIG =
  createHexVersionAtomicOperationDefinition({
    type: CREATE_DISPLAY_TABLE_CONFIG_TYPE,
    payloadSchema: CreateDisplayTableConfigPayload,
    readAuth: {
      kind: "hasSomeOfSemanticCaps",
      someOfCaps: [
        SemanticCap.VIEW_PROJECT_CONTENTS_FOR_LOGIC,
        SemanticCap.VIEW_PROJECT_CONTENTS_FOR_APP,
      ],
    },
    writeAuth: {
      kind: "and",
      and: [
        {
          kind: "hasSemanticCapOnIdArg",
          cap: SemanticCap.EDIT_PROJECT_CONTENTS,
          idArg: "cellId",
          idType: "Cell",
        },
        {
          kind: "idArgDoesNotExist",
          idArg: "displayTableConfigId",
          idType: "DisplayTableConfig",
        },
      ],
    },
    scopedIds: {
      cellId: { scope: "ownedByWrapper", idType: "Cell" },
      displayTableConfigId: {
        scope: "ownerUnresolvable",
        idType: "DisplayTableConfig",
        reason: "project-scoped, but carries no hexVersionId column",
      },
    },
    logSafe: ["cellId", "displayTableConfigId"],
    conflictId: (op) =>
      `${CREATE_DISPLAY_TABLE_CONFIG_TYPE}-${op.payload.displayTableConfigId}`,
    creationId: (op) => `${CREATE_CELL.type}-${op.payload.cellId}`,
    create: (args: CreateDisplayTableConfigPayload) => ({
      type: CREATE_DISPLAY_TABLE_CONFIG_TYPE,
      payload: {
        ...args,
      },
    }),
  });

export type CREATE_DISPLAY_TABLE_CONFIG = ExtractAtomicOperationFromDefinition<
  typeof CREATE_DISPLAY_TABLE_CONFIG
>;

const CREATE_SQL_CELL_DISPLAY_TABLE_CONFIG_TYPE =
  "CREATE_SQL_CELL_DISPLAY_TABLE_CONFIG" as const;
export const CreateSqlCellDisplayTableConfigPayload = z.object({
  cellId: zCellId,
  displayTableConfigId: zDisplayTableConfigId,
  sqlCellId: zSqlCellId,
  pageSize: z.number().optional(),
});
export type CreateSqlCellDisplayTableConfigPayload = z.infer<
  typeof CreateSqlCellDisplayTableConfigPayload
>;

export const CREATE_SQL_CELL_DISPLAY_TABLE_CONFIG =
  createHexVersionAtomicOperationDefinition({
    type: CREATE_SQL_CELL_DISPLAY_TABLE_CONFIG_TYPE,
    payloadSchema: CreateSqlCellDisplayTableConfigPayload,
    readAuth: {
      kind: "hasSemanticCap",
      cap: SemanticCap.VIEW_PROJECT_CONTENTS_FOR_LOGIC,
    },
    writeAuth: {
      kind: "and",
      and: [
        {
          kind: "hasSemanticCapOnIdArg",
          cap: SemanticCap.EDIT_PROJECT_CONTENTS,
          idArg: "cellId",
          idType: "Cell",
        },
        {
          kind: "hasSemanticCapOnIdArg",
          cap: SemanticCap.EDIT_PROJECT_CONTENTS,
          idArg: "sqlCellId",
          idType: "SqlCell",
        },
        {
          kind: "idArgDoesNotExist",
          idArg: "displayTableConfigId",
          idType: "DisplayTableConfig",
        },
      ],
    },
    scopedIds: {
      cellId: { scope: "ownedByWrapper", idType: "Cell" },
      displayTableConfigId: {
        scope: "ownerUnresolvable",
        idType: "DisplayTableConfig",
        reason: "project-scoped, but carries no hexVersionId column",
      },
      sqlCellId: { scope: "ownedByWrapper", idType: "SqlCell" },
    },
    logSafe: ["cellId", "displayTableConfigId", "sqlCellId"],
    conflictId: (op) =>
      `${CREATE_SQL_CELL_DISPLAY_TABLE_CONFIG_TYPE}-${op.payload.sqlCellId}`,
    creationId: (op) => `${CREATE_CELL.type}-${op.payload.cellId}`,
    create: (args: CreateSqlCellDisplayTableConfigPayload) => ({
      type: CREATE_SQL_CELL_DISPLAY_TABLE_CONFIG_TYPE,
      payload: {
        ...args,
      },
    }),
  });

export type CREATE_SQL_CELL_DISPLAY_TABLE_CONFIG =
  ExtractAtomicOperationFromDefinition<
    typeof CREATE_SQL_CELL_DISPLAY_TABLE_CONFIG
  >;

const CREATE_DBT_METRIC_CELL_DISPLAY_TABLE_CONFIG_TYPE =
  "CREATE_DBT_METRIC_CELL_DISPLAY_TABLE_CONFIG" as const;
export const CreateDbtMetricCellDisplayTableConfigPayload = z.object({
  cellId: zCellId,
  displayTableConfigId: zDisplayTableConfigId,
  dbtMetricCellId: zDbtMetricCellId,
});
export type CreateDbtMetricCellDisplayTableConfigPayload = z.infer<
  typeof CreateDbtMetricCellDisplayTableConfigPayload
>;

export const CREATE_DBT_METRIC_CELL_DISPLAY_TABLE_CONFIG =
  createHexVersionAtomicOperationDefinition({
    type: CREATE_DBT_METRIC_CELL_DISPLAY_TABLE_CONFIG_TYPE,
    payloadSchema: CreateDbtMetricCellDisplayTableConfigPayload,
    readAuth: {
      kind: "hasSemanticCap",
      cap: SemanticCap.VIEW_PROJECT_CONTENTS_FOR_LOGIC,
    },
    writeAuth: {
      kind: "and",
      and: [
        {
          kind: "hasSemanticCapOnIdArg",
          cap: SemanticCap.EDIT_PROJECT_CONTENTS,
          idArg: "cellId",
          idType: "Cell",
        },
        {
          kind: "hasSemanticCapOnIdArg",
          cap: SemanticCap.EDIT_PROJECT_CONTENTS,
          idArg: "dbtMetricCellId",
          idType: "DbtMetricCell",
        },
        {
          kind: "idArgDoesNotExist",
          idArg: "displayTableConfigId",
          idType: "DisplayTableConfig",
        },
      ],
    },
    scopedIds: {
      cellId: { scope: "ownedByWrapper", idType: "Cell" },
      displayTableConfigId: {
        scope: "ownerUnresolvable",
        idType: "DisplayTableConfig",
        reason:
          "TODO(FOUND-1750): project-scoped, but carries no hexVersionId column — it is reached through its cell. Needs a parent-walk resolver before this id can be bound.",
      },
      dbtMetricCellId: { scope: "ownedByWrapper", idType: "DbtMetricCell" },
    },
    logSafe: ["cellId", "displayTableConfigId", "dbtMetricCellId"],
    conflictId: (op) =>
      `${CREATE_DBT_METRIC_CELL_DISPLAY_TABLE_CONFIG_TYPE}-${op.payload.dbtMetricCellId}`,
    creationId: (op) => `${CREATE_CELL.type}-${op.payload.cellId}`,
    create: (args: CreateDbtMetricCellDisplayTableConfigPayload) => ({
      type: CREATE_DBT_METRIC_CELL_DISPLAY_TABLE_CONFIG_TYPE,
      payload: {
        ...args,
      },
    }),
  });

export type CREATE_DBT_METRIC_CELL_DISPLAY_TABLE_CONFIG =
  ExtractAtomicOperationFromDefinition<
    typeof CREATE_DBT_METRIC_CELL_DISPLAY_TABLE_CONFIG
  >;

export interface ColumnPropertiesUpdateableFields {
  displayFormat: ColumnDisplayFormat | null;
  size: number | null;
  wrapText: boolean;
  renameTo: string | null;
}
const _UpdateColumnPropertiesSafeFields: {
  [key in keyof ColumnPropertiesUpdateableFields]: "";
} = {
  displayFormat: "",
  size: "",
  wrapText: "",
  renameTo: "",
};
export const UpdateColumnPropertiesSafeFields = typedObjectKeys(
  _UpdateColumnPropertiesSafeFields,
);

const UPDATE_COLUMN_PROPERTIES_TYPE = "UPDATE_COLUMN_PROPERTIES" as const;
export const UpdateColumnPropertiesPayload = keyValueUpdatePayloadSchema({
  shared: {
    displayTableConfigId: zDisplayTableConfigId,
    cellId: zCellId,
    originalName: zDisplayTableColumnId,
  },
  fields: {
    displayFormat: zFromRuntype(ColumnDisplayFormat).nullable(),
    size: z.number().nullable(),
    wrapText: z.boolean(),
    renameTo: z.string().nullable(),
  } satisfies Record<keyof ColumnPropertiesUpdateableFields, z.ZodTypeAny>,
});
export type UpdateColumnPropertiesPayload = z.infer<
  typeof UpdateColumnPropertiesPayload
>;

export const UPDATE_COLUMN_PROPERTIES =
  createHexVersionAtomicOperationDefinition({
    type: UPDATE_COLUMN_PROPERTIES_TYPE,
    payloadSchema: UpdateColumnPropertiesPayload,
    readAuth: {
      kind: "hasSemanticCap",
      cap: SemanticCap.VIEW_PROJECT_CONTENTS_FOR_LOGIC,
    },
    writeAuth: {
      kind: "and",
      and: [
        {
          kind: "hasSemanticCapOnIdArg",
          cap: SemanticCap.EDIT_PROJECT_CONTENTS,
          idArg: "cellId",
          idType: "Cell",
        },
        {
          kind: "hasSemanticCapOnIdArg",
          idArg: "displayTableConfigId",
          idType: "DisplayTableConfig",
          cap: SemanticCap.EDIT_PROJECT_CONTENTS,
        },
      ],
    },
    scopedIds: {
      cellId: { scope: "ownedByWrapper", idType: "Cell" },
      displayTableConfigId: {
        scope: "ownerUnresolvable",
        idType: "DisplayTableConfig",
        reason: "project-scoped, but carries no hexVersionId column",
      },
    },
    logSafe: ["displayTableConfigId", "cellId", "key"],
    conflictId: (op) =>
      `${UPDATE_COLUMN_PROPERTIES_TYPE}-${op.payload.displayTableConfigId}-${op.payload.key}-${op.payload.originalName}`,
    creationId: (op) => `${CREATE_CELL.type}-${op.payload.cellId}`,
    create: <K extends keyof ColumnPropertiesUpdateableFields>(args: {
      displayTableConfigId: DisplayTableConfigId;
      cellId: CellId;
      originalName: DisplayTableColumnId;
      key: K;
      value: ColumnPropertiesUpdateableFields[K];
    }) => ({
      type: UPDATE_COLUMN_PROPERTIES_TYPE,
      payload: {
        ...args,
      },
    }),
  });

export type UPDATE_COLUMN_PROPERTIES = ExtractAtomicOperationFromDefinition<
  typeof UPDATE_COLUMN_PROPERTIES
>;

const RENAME_COLUMN_PROPERTIES_TYPE = "RENAME_COLUMN_PROPERTIES" as const;
/**
 * @deprecated - use UPDATE_COLUMN_PROPERTIES in bulk instead
 */
export const RenameColumnPropertiesPayload = z.object({
  displayTableConfigId: zDisplayTableConfigId,
  cellId: zCellId,
  originalName: zDisplayTableColumnId,
  newName: zDisplayTableColumnId,
  suppressAutoRun: z.boolean().optional(),
});
export type RenameColumnPropertiesPayload = z.infer<
  typeof RenameColumnPropertiesPayload
>;

export const RENAME_COLUMN_PROPERTIES =
  createHexVersionAtomicOperationDefinition({
    type: RENAME_COLUMN_PROPERTIES_TYPE,
    payloadSchema: RenameColumnPropertiesPayload,
    readAuth: {
      kind: "hasSomeOfSemanticCaps",
      someOfCaps: [
        SemanticCap.VIEW_PROJECT_CONTENTS_FOR_LOGIC,
        SemanticCap.VIEW_PROJECT_CONTENTS_FOR_APP,
      ],
    },
    writeAuth: {
      kind: "and",
      and: [
        {
          kind: "hasSemanticCapOnIdArg",
          cap: SemanticCap.EDIT_PROJECT_CONTENTS,
          idArg: "cellId",
          idType: "Cell",
        },
        {
          kind: "hasSemanticCapOnIdArg",
          idArg: "displayTableConfigId",
          idType: "DisplayTableConfig",
          cap: SemanticCap.EDIT_PROJECT_CONTENTS,
        },
      ],
    },
    scopedIds: {
      cellId: { scope: "ownedByWrapper", idType: "Cell" },
      displayTableConfigId: {
        scope: "ownerUnresolvable",
        idType: "DisplayTableConfig",
        reason: "project-scoped, but carries no hexVersionId column",
      },
    },
    logSafe: ["displayTableConfigId", "cellId"],
    conflictId: (op) =>
      `${RENAME_COLUMN_PROPERTIES_TYPE}-${op.payload.displayTableConfigId}`,
    creationId: (op) => `${CREATE_CELL.type}-${op.payload.cellId}`,
    create: (args: RenameColumnPropertiesPayload) => ({
      type: RENAME_COLUMN_PROPERTIES_TYPE,
      payload: {
        ...args,
      },
    }),
  });

/**
 * @deprecated - use UPDATE_COLUMN_PROPERTIES in bulk instead
 */
export type RENAME_COLUMN_PROPERTIES = ExtractAtomicOperationFromDefinition<
  typeof RENAME_COLUMN_PROPERTIES
>;

const CREATE_CHART_CELL_DISPLAY_TABLE_CONFIG_TYPE =
  "CREATE_CHART_CELL_DISPLAY_TABLE_CONFIG" as const;
export const CreateChartCellDisplayTableConfigPayload = z.object({
  cellId: zCellId,
  displayTableConfigId: zDisplayTableConfigId,
  chartCellId: zChartCellId,
  skipCellRun: z.boolean().optional(),
});
export type CreateChartCellDisplayTableConfigPayload = z.infer<
  typeof CreateChartCellDisplayTableConfigPayload
>;

export const CREATE_CHART_CELL_DISPLAY_TABLE_CONFIG =
  createHexVersionAtomicOperationDefinition({
    type: CREATE_CHART_CELL_DISPLAY_TABLE_CONFIG_TYPE,
    payloadSchema: CreateChartCellDisplayTableConfigPayload,
    readAuth: {
      kind: "hasSemanticCap",
      cap: SemanticCap.VIEW_PROJECT_CONTENTS_FOR_LOGIC,
    },
    writeAuth: {
      kind: "and",
      and: [
        {
          kind: "hasSemanticCapOnIdArg",
          cap: SemanticCap.EDIT_PROJECT_CONTENTS,
          idArg: "cellId",
          idType: "Cell",
        },
        {
          kind: "hasSemanticCapOnIdArg",
          cap: SemanticCap.EDIT_PROJECT_CONTENTS,
          idArg: "chartCellId",
          idType: "ChartCell",
        },
        {
          kind: "idArgDoesNotExist",
          idArg: "displayTableConfigId",
          idType: "DisplayTableConfig",
        },
      ],
    },
    scopedIds: {
      cellId: { scope: "ownedByWrapper", idType: "Cell" },
      displayTableConfigId: {
        scope: "ownerUnresolvable",
        idType: "DisplayTableConfig",
        reason: "project-scoped, but carries no hexVersionId column",
      },
      chartCellId: { scope: "ownedByWrapper", idType: "ChartCell" },
    },
    logSafe: ["cellId", "displayTableConfigId", "chartCellId"],
    conflictId: (op) =>
      `${CREATE_CHART_CELL_DISPLAY_TABLE_CONFIG_TYPE}-${op.payload.chartCellId}`,
    creationId: (op) => `${CREATE_CELL.type}-${op.payload.cellId}`,
    create: (args: CreateChartCellDisplayTableConfigPayload) => ({
      type: CREATE_CHART_CELL_DISPLAY_TABLE_CONFIG_TYPE,
      payload: {
        ...args,
      },
    }),
  });

export type CREATE_CHART_CELL_DISPLAY_TABLE_CONFIG =
  ExtractAtomicOperationFromDefinition<
    typeof CREATE_CHART_CELL_DISPLAY_TABLE_CONFIG
  >;
